/**
 * Consent state.
 *
 * The principle for this site is to minimise what needs consent, then
 * implement consent properly for whatever remains. Today nothing on this site
 * needs consent:
 *
 *   vote_id        set only at the first vote or submission — strictly
 *                  necessary for a service the user explicitly requested
 *   admin_session  authentication, strictly necessary
 *   snapshot cache caches content the user requested
 *   notice_seen    remembers the notice was dismissed
 *
 * So the default UI is a non-blocking information notice, not a gate.
 *
 * The consent layer below is fully built and switched off behind
 * VITE_CONSENT_REQUIRED. The moment anything non-essential is added —
 * analytics, a pixel, an embedded video, a web font from someone else's
 * server — that flag must be turned on in the same commit, because from that
 * moment the site is setting storage that GDPR Art. 6(1)(a) and § 89 ZEK
 * require consent for.
 */

/** Bump only when the policy materially changes; never on a schedule. */
export const CONSENT_VERSION = 1;

export const CONSENT_STORAGE_KEY = 'consent_state';
export const NOTICE_STORAGE_KEY = 'notice_seen';

export interface ConsentState {
  version: number;
  timestamp: string;
  /** Always true, never presented as a toggle — recorded for completeness. */
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}

export const CONSENT_REQUIRED = import.meta.env.VITE_CONSENT_REQUIRED === 'true';

/** localStorage that cannot throw. Private mode and blocked storage both throw. */
function safeRead(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // A visitor who blocks storage simply sees the notice again next time.
    // That is the correct outcome, not an error to report.
  }
}

export function readConsent(): ConsentState | null {
  const raw = safeRead(CONSENT_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ConsentState;
    // Re-prompt only when the policy version increments.
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeConsent(choice: { analytics: boolean; marketing: boolean }): ConsentState {
  const state: ConsentState = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    necessary: true,
    ...choice,
  };
  safeWrite(CONSENT_STORAGE_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent('consentchange', { detail: state }));
  return state;
}

/**
 * Whether a non-essential category may load. Every gated script must call
 * this before injecting anything — gating behaviour alone is not enough,
 * because a loaded tracker has already set storage and phoned home by the
 * time its behaviour is suppressed.
 */
export function hasConsent(category: 'analytics' | 'marketing'): boolean {
  if (!CONSENT_REQUIRED) {
    // With the layer off, nothing non-essential exists to load. Returning
    // false rather than true means that if something non-essential is added
    // without turning the flag on, it stays blocked instead of running
    // unconsented.
    return false;
  }
  return readConsent()?.[category] === true;
}

/** True when the panel should be shown unprompted. */
export function needsConsentPrompt(): boolean {
  return CONSENT_REQUIRED && readConsent() === null;
}

export function noticeDismissed(): boolean {
  return safeRead(NOTICE_STORAGE_KEY) === String(CONSENT_VERSION);
}

export function dismissNotice(): void {
  safeWrite(NOTICE_STORAGE_KEY, String(CONSENT_VERSION));
}

/** Opened from the permanent footer link. Withdrawal is as easy as granting. */
export function openConsentSettings(): void {
  window.dispatchEvent(new CustomEvent('consent:open'));
}
